<template>
  <v-card :elevation="0">
    <v-row>
      <v-col>
        You are about to close <strong>{{ caseTitle }}</strong>. In order to close, we need to ask you a few questions.
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-autocomplete
          v-if="settingsClosingReasonsDefined"
          v-model="reasonsIds"
          item-text="label"
          item-value="id"
          :items="settingsClosingReasons"
          label="Why are you closing this case?"
          :error="v$.reasonsIds.$error"
          chips
          clearable
          deletable-chips
          filled
          multiple
          @change="changeClosingReasonsIds"
        />
        <v-alert
          v-else
          dense
          border="left"
          type="warning"
          icon="mdi-alert-outline"
        >
          You have to define closing reasons first. Go to <strong>Settings</strong> > <strong>Cases</strong> >
          <strong>Closing Reasons</strong>. Then you can proceed.
        </v-alert>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <ChecklistViewer
          v-if="legalCase.checklist"
          :checklist="legalCase.checklist"
          @update="updateChecklist"
        />

        <p
          v-if="!legalCase.checklist_completed"
          class="error--text"
        >
          You have not completed all the case milestones, please explain why in additional notes.
        </p>

        <v-textarea
          v-model="notes"
          label="Additional Notes"
          :error="v$.notes.$error"
          filled
          @change="changeClosingNotes"
        >
          <template
            v-if="notesRequired"
            #label
          >
            Additional Notes<span class="error--text"><strong> *</strong></span>
          </template>
        </v-textarea>

        <div v-if="v$.$errors">
          <p
            v-for="error of v$.$errors"
            :key="error.$uid"
            class="error--text"
          >
            <strong>{{ error.$message }}</strong>
          </p>
        </div>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import { helpers, required, requiredIf } from '@vuelidate/validators'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import useVuelidate from '@vuelidate/core'

import { LEGAL_CASE_NAMESPACE } from '@/store/legal-case'
import { SETTINGS_NAMESPACE } from '@/store/settings'

import ChecklistViewer from '@/components/Checklist/ChecklistViewer.vue'

export default {
  name: 'CaseClosingDetails',

  components: {
    ChecklistViewer,
  },

  props: {
    legalCase: {
      required: true,
      type: Object,
    },
  },

  setup () {
    return {
      v$: useVuelidate(),
    }
  },

  data () {
    return {
      notes: null,
      reasonsIds: [],
    }
  },

  validations () {
    return {
      notes: {
        requiredIfOther: helpers.withMessage('Please provide additional notes', requiredIf(this.notesRequired)),
      },

      reasonsIds: {
        required: helpers.withMessage('Please provide at least one reason', required),
      },
    }
  },

  computed: {
    caseTitle () {
      return this.legalCaseTypesIds[this.legalCase?.legal_case_type_id]?.fullName
    },

    notesRequired () {
      return (
        (this.legalCase?.checklist && !this.legalCase?.checklist_completed) ||
          this.reasonsIds.includes('-1')
      )
    },

    settingsClosingReasonsDefined () {
      // By default, we hard set one reason (name 'Other').
      // However we want to check for user created reasons.
      return this.settingsClosingReasons.length > 1
    },

    ...mapGetters([
      'legalCaseTypesIds',
    ]),

    /* eslint-disable-next-line llp/vuex-use-arrays */
    ...mapGetters(SETTINGS_NAMESPACE, {
      settingsClosingReasons: 'closingReasons',
    }),
  },

  created () {
    this.settingsFetchClosingReasons()
  },

  methods: {
    changeClosingNotes (value) {
      this.setClosingNotes(value)
      this.updateFormValidity()
    },

    changeClosingReasonsIds (value) {
      this.setClosingReasonsIds(value)
      this.updateFormValidity()
    },

    updateChecklist (options) {
      this.updateLegalCaseChecklist({
        ...options.data,
        legalCaseId: this.legalCase.id,
      }).
        then(this.updateFormValidity).
        catch(options.errorCallback).
        finally(options.finalCallback)
    },

    async updateFormValidity () {
      await this.v$.$validate()
      this.$emit('update-step-validity', !this.v$.$invalid)
    },

    ...mapActions(LEGAL_CASE_NAMESPACE, [
      'updateLegalCaseChecklist',
    ]),

    /* eslint-disable-next-line llp/vuex-use-arrays */
    ...mapActions(SETTINGS_NAMESPACE, {
      settingsFetchClosingReasons: 'fetchClosingReasons',
    }),

    ...mapMutations(LEGAL_CASE_NAMESPACE, [
      'setClosingNotes',
      'setClosingReasonsIds',
    ]),
  },
}
</script>
